import rfidClient from '../api/rfidClient';
import type { MovementConfig } from '../config/movements';
import { buildSubmitPayload, parseSubmitResult } from './rfidPayloadBuilders';
import {
  parseGetFlowToCustomersResponse,
  type TransferCustomerRow,
} from '../utils/transferCustomers';
import type { KitGroup, TransportDetails, WizardLine } from '../types/wizard';
import type { KitBomItem } from '../utils/tagAllocation';
import { parseStorageLocationsResponse } from '../utils/storageLocations';
import { normalizeStorageLocationPlant } from '../utils/document';
import type { StorageLocationOption } from '../utils/storageLocations';
import {
  parseMaterialDetailsResponse,
  type MaterialMasterOption,
} from '../utils/materialMaster';
import type { WizardVariant } from '../types/wizard';
import {
  buildCustomerPostingContext,
  customerCodeForSapApi,
  parseCustomerDetailsResponse,
  type CustomerPostingContext,
} from '../utils/customerDetails';

export type RfidDocumentRow = {
  DocumentNo?: string;
  PostingDate?: string;
  CustName?: string;
  Sender?: string;
  SenderName?: string;
  Receiver?: string;
  ReceiverName?: string;
  Plant?: string;
  PlantName?: string;
  Customer?: string;
  TransporterCode?: string;
  TransporterName?: string;
  TRANSPORT_NAME?: string;
  VehicleNo?: string;
  VehicleType?: string;
  DriverName?: string;
  DriverMobileNo?: string;
  InspectedBy?: string;
  LR?: string;
  Distance?: string;
  Reason?: string;
  ContainerSeal?: string;
  EwayTranstype?: string;
  ModeofTransport?: string;
  Items?: { Item?: unknown[] | Record<string, unknown> };
  [key: string]: unknown;
};

type SoListResponse = {
  Response?: {
    Document?: RfidDocumentRow | RfidDocumentRow[];
  };
};

export function countDocumentItems(items: RfidDocumentRow['Items']): number {
  const item = items?.Item;
  if (item == null) return 0;
  return Array.isArray(item) ? item.length : 1;
}

function normalizeDocuments(raw: RfidDocumentRow | RfidDocumentRow[] | undefined): RfidDocumentRow[] {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return [...list].sort((a, b) => {
    const da = String(a.PostingDate ?? '');
    const db = String(b.PostingDate ?? '');
    return db.localeCompare(da);
  });
}

function buildListPayload(movement: MovementConfig, employeeCode: string) {
  if (movement.usesUserIdOnly) {
    return { Request_Data: { UserId: employeeCode } };
  }
  return {
    Request_Data: {
      UserID: employeeCode,
      ...(movement.docType ? { DocType: movement.docType } : {}),
    },
  };
}

function unwrapList(data: unknown): RfidDocumentRow[] {
  const d = data as SoListResponse & Record<string, unknown>;
  if (d?.Response?.Document) return normalizeDocuments(d.Response.Document);
  const rd = d?.Response_Data as { Document?: RfidDocumentRow | RfidDocumentRow[] } | undefined;
  if (rd?.Document) return normalizeDocuments(rd.Document);
  return [];
}

export const rfidService = {
  async fetchDocumentList(movement: MovementConfig, employeeCode: string): Promise<RfidDocumentRow[]> {
    if (!movement.apiPath) return [];
    const payload = buildListPayload(movement, employeeCode);
    const { data } = await rfidClient.post<unknown>(movement.apiPath, payload);
    return unwrapList(data);
  },

  /** Storage bays for From Bay picker (RFID API → SAP CPI LIPLMM497). */
  async fetchStorageLocations(plant: string): Promise<StorageLocationOption[]> {
    const plantCode = normalizeStorageLocationPlant(plant);
    if (!plantCode) return [];
    const payload = {
      Request_Data: {
        item: [{ Plant: plantCode }],
      },
    };

    const { data } = await rfidClient.post<unknown>('/FetchStorageLocation', payload);
    return parseStorageLocationsResponse(data);
  },

  /** Material master for Engine Tag SKU picker (SAP CPI P4/I130). */
  async fetchMaterialDetails(customerCode = ''): Promise<MaterialMasterOption[]> {
    const { data } = await rfidClient.post<unknown>('/Fetch_MaterialDetails', {
      Request_Data: { CustomerCode: customerCode.trim() },
    });
    return parseMaterialDetailsResponse(data);
  },

  /** Customer LOB + Account_Group for AUT vs manual posting (SAP fetchCustDetails). */
  async fetchCustomerDetails(customerCode: string): Promise<CustomerPostingContext> {
    const code = customerCodeForSapApi(customerCode);
    const { data } = await rfidClient.post<unknown>('/customer_details', {
      Request_Data: { CustomerCode: code },
    });
    const row = parseCustomerDetailsResponse(data, customerCode);
    if (!row) {
      throw new Error('Customer details not found for this customer number.');
    }
    return buildCustomerPostingContext(row);
  },

  async fetchKitBom(documentNo: string): Promise<Record<string, unknown>[]> {
    const { data } = await rfidClient.post<unknown>('/GetKitBOM', {
      Request_Data: { SalesOrderNo: documentNo },
    });
    const d = data as Record<string, unknown>;
    const rd = d.Response_Data as Record<string, unknown> | undefined;
    const order = rd?.Order as Array<Record<string, unknown>> | undefined;
    const first = order?.[0];
    const toItems = first?.to_Items as Record<string, unknown> | undefined;
    const items = toItems?.Item;
    if (!items) return [];
    return Array.isArray(items) ? (items as Record<string, unknown>[]) : [items as Record<string, unknown>];
  },

  async postDocument(
    movement: MovementConfig,
    ctx: {
      userId: string;
      document: RfidDocumentRow;
      effectiveDate: Date;
      transport: TransportDetails;
      lines: WizardLine[];
      kitGroups: KitGroup[];
      kitBom: KitBomItem[] | null;
      remarks: string;
      variant: WizardVariant;
      transferBpCode?: string;
      transferToBpCode?: string;
      transferToCustomerName?: string;
      transferCustomerName?: string;
      transferDetails?: import('../types/wizard').TransferDetails;
      transferLineOfBusiness?: string;
      transferBaseDocNo?: string;
      useAutoTransferType: boolean;
    }
  ): Promise<{ success: boolean; message: string; raw: unknown }> {
    const payload = buildSubmitPayload(movement, ctx);
    const { data } = await rfidClient.post<unknown>(movement.submitPath, payload);
    const parsed = parseSubmitResult(data);
    return { success: parsed.success, message: parsed.message, raw: data };
  },

  /** Destination customers for a selected from-customer (legacy GetFlow_ToCustomers + Customer_No). */
  async fetchTransferToCustomers(fromBpCode: string): Promise<TransferCustomerRow[]> {
    const padded = fromBpCode.trim().padStart(10, '0');
    const { data } = await rfidClient.post<unknown>('/GetFlow_ToCustomers', {
      Request_Data: { Customer_No: padded },
    });
    return parseGetFlowToCustomersResponse(data);
  },

  async fetchTransferItems(
    fromBpCode: string,
    toCustomerCode: string
  ): Promise<Record<string, unknown>[]> {
    const { data } = await rfidClient.post<unknown>('/Get_ItemDetails', {
      Request_Data: {
        From_Customer: fromBpCode,
        To_Customer: toCustomerCode,
      },
    });
    const d = data as Record<string, unknown>;
    const rd = d.Response_Data as Record<string, unknown> | undefined;
    const itemsWrap = rd?.Items as Record<string, unknown> | undefined;
    const raw = itemsWrap?.Item;
    if (!raw) return [];
    return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : [raw as Record<string, unknown>];
  },

  async fetchTransferBaseDocs(bpCode: string): Promise<Record<string, unknown>[]> {
    const padded = bpCode.trim().padStart(10, '0');
    const { data } = await rfidClient.post<unknown>('/Get_BaseDoc', {
      Request_Data: { CustomerNo: padded },
    });
    const d = data as Record<string, unknown>;
    const rd = d.Response_Data as Record<string, unknown> | undefined;
    const raw = rd?.Item ?? rd?.Document;
    if (!raw) return [];
    const list = Array.isArray(raw) ? (raw as Record<string, unknown>[]) : [raw as Record<string, unknown>];
    const receiver = padded;
    return list.filter((doc) => {
      const recv = String(doc.RECIEVER ?? doc.RECEIVER ?? '').trim();
      const sender = String(doc.SENDER ?? '').trim();
      return recv === receiver && sender !== receiver;
    });
  },
};
